<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";
import AppModal from "@/components/ui/AppModal.vue";
import IsicTable from "@/domains/home/services/configurations/views/nature_of_business/IsicTable.vue";
import IsicList from "@/domains/home/services/configurations/views/nature_of_business/IsicList.vue";
import { useToast } from "@/composables";

interface IsicItem {
  text: string
  value: string
}

interface IsicForm {
  code: string | null
  description: string | null
  parentId: string | null
}

const store = useConfigurationsStore();
const { successToast, errorToast } = useToast()

const showCreate = ref(false)
const search = ref<string | null>(null)
const isTable = ref(true)
const loading = ref(false)
const IsicItems = ref<IsicItem[]>([])

const form = ref<IsicForm>({
  code: null,
  description: null,
  parentId: null
})

onMounted(() => {
  store.fetchIsic();
})

const submitIsicForm = () => {
  loading.value = true
  store.addIsicCategory(form.value);
}

const openForm = () => {
  showCreate.value = true
}

watch(() => store.isicAdd, () => {
  loading.value = false
  successToast()
  showCreate.value = false
  store.fetchIsic();
})

watch(() => store.isicError, (error) => {
  loading.value = false
  errorToast("Invalid", error.message)
})

watch(() => store.isicList, (list) => {
  if (list && Array.isArray(list)) {
    IsicItems.value = list.map(item => ({
      text: item.description,
      value: item.id
    }))
  }
})
</script>

<template>
  <div class="flex-1 flex flex-col p-4">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-base font-semibold text-gray-800">ISIC Categories</h2>
      <p class="text-sm text-gray-500 mt-1">Manage International Standard Industrial Classification categories used to
        classify the nature of business for registered entities.</p>
    </div>

    <!-- Add Category Modal -->
    <AppModal v-model="showCreate" title="Add Category" hide-footer>
      <div class="p-4">
        <form @submit.prevent="submitIsicForm" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <i class="fa-solid fa-align-left mr-1"></i>
              Category Description *
            </label>
            <input
              type="text"
              v-model="form.description"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              placeholder="Category description"
              required
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <i class="fa-solid fa-code mr-1"></i>
              Code *
            </label>
            <input
              type="text"
              v-model="form.code"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              placeholder="Code"
              required
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <i class="fa-solid fa-sitemap mr-1"></i>
              ISIC Category
            </label>
            <select
              v-model="form.parentId"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option :value="null">None (Root Level)</option>
              <option
                v-for="option in IsicItems"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>

          <div class="flex justify-end">
            <button
              type="submit"
              class="btn-primary"
              :disabled="loading"
            >
              <i class="fa fa-save mr-1"></i>
              <span v-if="!loading">Save</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Action Bar -->
    <div class="flex items-center gap-3 mb-4">
      <div class="flex-1 relative">
        <input
          type="text"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
          v-model="search"
          placeholder="Search categories..."
        />
        <i class="fas fa-search absolute right-3 top-3 text-gray-400"></i>
      </div>

      <div class="flex items-center gap-1">
        <button
          @click="isTable = true"
          class="px-3 py-2 rounded text-sm transition"
          :class="isTable ? 'text-primary' : 'text-gray-300'"
          title="Table View"
        >
          <i class="fa fa-table text-xl"></i>
        </button>
        <button
          @click="isTable = false"
          class="px-3 py-2 rounded text-sm transition"
          :class="!isTable ? 'text-primary' : 'text-gray-300'"
          title="List View"
        >
          <i class="fa fa-bars text-xl"></i>
        </button>
      </div>

      <button @click="openForm" class="btn-primary whitespace-nowrap">
        <i class="fa fa-plus"></i>
        <span>Add Category</span>
      </button>
    </div>

    <!-- Content View -->
    <div class="flex-1">
      <IsicTable v-if="isTable" :search="search" />
      <IsicList v-else />
    </div>
  </div>
</template>
